
/**
 * Sale Badge Checker
 * 
 * Automatically checks and displays "Sale" badges for products using Storefront API
 * Works on product cards (class "card-product" with data-product-id) and PDP
 * Uses MutationObserver to detect dynamically added product cards
 * Caches API responses to avoid redundant calls
 */

(function() {
  'use strict';

  const STOREFRONT_TOKEN = 'f03bcbe287205c23fd45fdd8609eefb1';
  const CHECKED_ATTR = 'data-sale-badge-checked';
  const PROCESSING_ATTR = 'data-sale-badge-processing';
  
  // Shared cache across all instances
  const productPriceCache = window.productPriceCache || new Map();
  window.productPriceCache = productPriceCache;

  async function fetchProductPricing(productId) {
    if (productPriceCache.has(productId)) {
      return productPriceCache.get(productId);
    }

    const query = `
      query ProductPricing($productId: ID!) {
        product(id: $productId) {
          id
          variants(first: 100) {
            edges {
              node {
                id
                price {
                  amount
                  currencyCode
                }
                compareAtPrice {
                  amount
                  currencyCode
                }
                availableForSale
              }
            }
          }
        }
      }
    `;

    try {
      const productGid = `gid://shopify/Product/${productId}`;
      const res = await fetch(`/api/2025-01/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN,
        },
        body: JSON.stringify({
          query,
          variables: { productId: productGid },
        }),
      });

      const json = await res.json();
      if (json.errors) {
        console.error('Storefront API error:', json.errors);
        return null;
      }

      const variants = json?.data?.product?.variants?.edges || [];
      const pricingData = variants.map((edge) => ({
        id: edge.node.id,
        price: parseFloat(edge.node.price?.amount || 0),
        compareAtPrice: edge.node.compareAtPrice ? parseFloat(edge.node.compareAtPrice.amount || 0) : null,
        availableForSale: edge.node.availableForSale,
      }));

      productPriceCache.set(productId, pricingData);
      return pricingData;
    } catch (error) {
      console.error('Error fetching product pricing:', error);
      return null;
    }
  }

  /**
   * Check if a product is on sale based on pricing data
   * @param {Array} pricingData - Array of variant pricing data
   * @returns {boolean} - true if product has any variant on sale
   */
  function checkIsOnSale(pricingData) {
    if (!pricingData || pricingData.length === 0) {
      return false;
    }

    // Check if any variant has compare_at_price > price
    return pricingData.some((variant) => {
      if (!variant.availableForSale) return false;
      return variant.compareAtPrice !== null && variant.compareAtPrice > variant.price;
    });
  }

  /**
   * Check if a product has automatic discounts applied in cart
   * DEPRECATED: This function is no longer used. Automatic discounts are now handled
   * by price-automatic-discounts.js which adds/removes classes on the badge container.
   * @param {string|number} productId - Product ID
   * @returns {Promise<boolean>} - Always returns false
   */
  async function checkCartDiscounts(productId) {
    return false;
  }

  /**
   * Check if a sale badge already exists in the container
   * @param {HTMLElement} container - The badge container element
   * @returns {HTMLElement|null} - Existing sale badge or null
   */
  function findExistingSaleBadge(container) {
    if (!container) return null;

    // Check for badge with data attribute
    let saleBadge = container.querySelector('[data-sale-badge]');
    if (saleBadge) return saleBadge;

    // Check for badge with "Sale" or "on sale" text
    const allBadges = container.querySelectorAll('.badge--header, .badge');
    for (const badge of allBadges) {
      const badgeText = badge.textContent.trim().toLowerCase();
      // Check if it contains "sale" or has the sale icon SVG
      if (badgeText.includes('sale') || badgeText.includes('on sale') || badge.querySelector('svg')) {
        // Verify it's actually a sale badge by checking for sale icon or sale-related classes
        const hasSaleIcon = badge.querySelector('svg path[d*="M7.176"]') || badge.querySelector('svg path[d*="7.176"]');
        if (hasSaleIcon || badgeText.includes('sale')) {
          return badge;
        }
      }
    }

    return null;
  }

  /**
   * Find or create sale badge element in a badge container
   * @param {HTMLElement} container - The logo-and-badges element
   * @returns {HTMLElement|null} - The badge element or null if not found/created
   */
  function findOrCreateSaleBadge(container) {
    if (!container) return null;

    // First check if sale badge already exists - don't create duplicate
    const existingSaleBadge = findExistingSaleBadge(container);
    if (existingSaleBadge) {
      return existingSaleBadge;
    }

    // If no sale badge exists, create one matching the structure in product-badges.liquid
    const saleBadge = document.createElement('span');
    saleBadge.className = 'badge--header';
    saleBadge.setAttribute('data-sale-badge', 'true');
    saleBadge.style.cssText = `
      --background-color: var(--color-red);
      --color: #ffffff;
      display: none;
    `;
    saleBadge.innerHTML = `
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M7.176 2.25H3.9375C3.48995 2.25 3.06072 2.42779 2.74426 2.74426C2.42779 3.06072 2.25 3.48995 2.25 3.9375V7.176C2.25 7.62375 2.42775 8.0535 2.74425 8.36925L9.93 15.555C10.4542 16.0792 11.265 16.209 11.8853 15.8025C13.4465 14.78 14.78 13.4465 15.8025 11.8853C16.209 11.265 16.0792 10.4542 15.555 9.93L8.37 2.745C8.21327 2.58808 8.02715 2.46359 7.82227 2.37865C7.61739 2.29372 7.39778 2.25 7.176 2.25Z" stroke="white" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M4.5 4.5H4.5066V4.5066H4.5V4.5Z" stroke="white" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
      Sale
    `;
    
    // Insert at the beginning of the container (before other badges)
    container.insertBefore(saleBadge, container.firstChild);

    return saleBadge;
  }

  /**
   * Process sale badge for a badge container
   * @param {HTMLElement} container - The logo-and-badges element
   */
  async function processBadgeContainer(container) {
    // Skip if already checked or currently processing
    if (container.hasAttribute(CHECKED_ATTR) || container.hasAttribute(PROCESSING_ATTR)) {
      return;
    }

    const productId = container.getAttribute('data-product-id');
    if (!productId) return;

    // Mark as processing to avoid duplicate checks
    container.setAttribute(PROCESSING_ATTR, 'true');

    try {
      const pricingData = await fetchProductPricing(parseInt(productId, 10));
      const isOnSaleFromPrice = checkIsOnSale(pricingData);
      
      // Automatic discounts are now handled by price-automatic-discounts.js
      // Only check compare_at_price > price for sale badge
      const isOnSale = isOnSaleFromPrice;
      
      // Find or create sale badge (will return existing one if found)
      const saleBadge = findOrCreateSaleBadge(container);
      if (saleBadge) {
        if (isOnSale) {
          saleBadge.style.display = 'flex';
        } else {
          // Only hide if we created it (has data-sale-badge), don't hide existing badges from Liquid
          if (saleBadge.hasAttribute('data-sale-badge')) {
            saleBadge.style.display = 'none';
          }
        }
      }
    } catch (error) {
      console.error('Error processing sale badge:', error);
    } finally {
      // Mark as checked and remove processing flag
      container.setAttribute(CHECKED_ATTR, 'true');
      container.removeAttribute(PROCESSING_ATTR);
    }
  }

  /**
   * Process sale badge for PDP (Product Detail Page)
   * Uses the logo-and-badges element
   */
  async function processPDPBadge() {
    // Find the logo-and-badges in PDP
    const container = document.querySelector('.logo-and-badges[data-product-id]');
    if (!container) {
      // Try again after a delay if container not found
      setTimeout(() => {
        const retryContainer = document.querySelector('.logo-and-badges[data-product-id]');
        if (retryContainer) {
          processBadgeContainer(retryContainer);
        }
      }, 1000);
      return;
    }

    // Process using the same function as other containers
    await processBadgeContainer(container);
  }

  function checkAllBadgeContainers() {
    // Find all badge containers that haven't been checked yet
    // Exclude Algolia search results and PLP (Product Listing Page)
    const isSearchPage = window.location.pathname.includes('/search');
    const isCollectionPage = window.location.pathname.includes('/collections/');
    const isPLP = isCollectionPage && !window.location.pathname.match(/\/collections\/[^\/]+\/products\//);

    // Only process containers on non-search, non-PLP pages
    if (isSearchPage || isPLP) return;

    // Find all logo-and-badges and card__badge_header elements
    const allContainers = document.querySelectorAll(`
      .logo-and-badges[data-product-id]:not([${CHECKED_ATTR}]):not([${PROCESSING_ATTR}]),
      .card__badge_header[data-product-id]:not([${CHECKED_ATTR}]):not([${PROCESSING_ATTR}])
    `);

    if (allContainers.length === 0) return;

    // Process containers in batches to avoid overwhelming the API
    const batchSize = 3;
    for (let i = 0; i < allContainers.length; i += batchSize) {
      const batch = Array.from(allContainers).slice(i, i + batchSize);
      batch.forEach(container => processBadgeContainer(container));
    }
  }

  // Initialize on DOM ready
  function init() {
    // Check existing badge containers
    checkAllBadgeContainers();

    // Process PDP badge if on product page
    if (window.location.pathname.includes('/products/')) {
      // Try multiple times with increasing delays to ensure container is loaded
      setTimeout(() => processPDPBadge(), 300);
      setTimeout(() => processPDPBadge(), 800);
      setTimeout(() => processPDPBadge(), 1500);
    }

    // Use MutationObserver to detect dynamically added badge containers
    const observer = new MutationObserver((mutations) => {
      let shouldCheck = false;
      
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) { // Element node
            // Check if the added node is a badge container or contains badge containers
            if (node.matches && (
              node.matches('.logo-and-badges[data-product-id]') ||
              node.matches('.card__badge_header[data-product-id]') ||
              node.querySelector('.logo-and-badges[data-product-id]') ||
              node.querySelector('.card__badge_header[data-product-id]')
            )) {
              shouldCheck = true;
            }
          }
        });
      });

      if (shouldCheck) {
        // Debounce checks to avoid excessive API calls
        clearTimeout(window.saleBadgeCheckTimeout);
        window.saleBadgeCheckTimeout = setTimeout(() => {
          checkAllBadgeContainers();
        }, 200);
      }
    });

    // Observe the entire document for changes
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Re-check when cart is updated (products might be added/removed or discounts applied)
    window.addEventListener('cart:updated', () => {
      // Clear cache to force re-check of discounts
      productPriceCache.clear();
      setTimeout(() => {
        checkAllBadgeContainers();
        // Also re-check PDP badge
        if (window.location.pathname.includes('/products/')) {
          processPDPBadge();
        }
      }, 300);
    });

    window.addEventListener('cart:refresh', () => {
      // Clear cache to force re-check of discounts
      productPriceCache.clear();
      setTimeout(() => {
        checkAllBadgeContainers();
        // Also re-check PDP badge
        if (window.location.pathname.includes('/products/')) {
          processPDPBadge();
        }
      }, 300);
    });

    // Listen to custom cart update events if available
    if (window.PUB_SUB_EVENTS && typeof subscribe === 'function') {
      subscribe(window.PUB_SUB_EVENTS.cartUpdate, () => {
        setTimeout(() => {
          checkAllBadgeContainers();
          if (window.location.pathname.includes('/products/')) {
            processPDPBadge();
          }
        }, 500);
      });
    }
  }

  // Start when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
